/**
 * User: itziky
 * Date: 6/26/13
 * Responsible to keep user session alive and to handle session expiration.
 */
define(function (require) {
    "use strict";

    var LEConfig = require("assets/config/le-env-conf");
    var CONST = require("const");
    var Logger = require("logger");

    var SessionKeepAlive = (function () {
        var AUTHENTICATION_CHECK_INTERVAL = LEConfig.Session.KeepAliveInterval * 1000;
        var APP_SERVER_KEEP_ALIVE_SESSION_INTERVAL = LEConfig.Session.AppServerKeepAliveInterval * 1000;
        var MAGIC_NUMBER_TIME_DIFF = 30 * 1000;

        function _doKeepAlive() {
            /*jshint validthis: true */
            this.logger.debug("_doKeepAlive: Going to refresh the session");
            this.sessionManager.sustain(null, {
                success: start.bind(this),
                error: start.bind(this)
            });
        }

        function _checkAuthentication() {
            /*jshint validthis: true */
            // First see if we are authenticated
            this.logger.debug("_checkAuthentication: It is time to check if the session should be refreshed");
            if (this.sessionManager.isAuthenticated()) {
                this.logger.debug("_checkAuthentication: Current session is valid");
                // Keep alive the session in case the touched flag is set to true
                if (this.isTouched) {
                    this.logger.debug("_checkAuthentication: User is active, Going to refresh the session");
                    _doKeepAlive.call(this);
                }
                else {
                    this.logger.debug("_checkAuthentication: User is inactive, Wait for next check");
                }

                this.logger.debug("_checkAuthentication: Set next check in " + AUTHENTICATION_CHECK_INTERVAL + " millis");
                this.authTimeoutId = setTimeout(_checkAuthentication.bind(this), AUTHENTICATION_CHECK_INTERVAL);
            } else {
                this.logger.debug("_checkAuthentication: Current session is invalid, Going to force logout");
                // Probably logout operations performed from other tabs of the same browser
                _forceSessionExpire.call(this, { noDestroyForCurrent: true, errorCode:CONST.AUTH_ERROR.ERROR_LOGIN_DIFFERENT_BROWSER });
            }
        }

        function _appServerKeepAlive() {
            /*jshint validthis: true */
            // First see if we are authenticated
            this.logger.debug("_appServerKeepAlive: It is time to check if the appServer session should be refreshed");
            if (this.sessionManager.isAuthenticated()) {
                this.logger.debug("_appServerKeepAlive: Current appServer session is valid");
                this.sessionManager.appServerSustain(null, {
                    success: startAppServerKeepAlive.bind(this),
                    error: _stopAppServerKeepAlive.bind(this)
                });
            } else {
                this.logger.debug("_keepAliveAppServerSession: Current app server session is invalid");
                _stopAppServerKeepAlive.call(this);
            }
        }

        function _stopAppServerKeepAlive() {
            /*jshint validthis: true */
            this.logger.debug("_stopAppServerKeepAlive: Stop app server session");
            if (this.appServerKeepAliveTimeoutId) {
                // Clear remaining keep alive appServer session timer
                clearTimeout(this.appServerKeepAliveTimeoutId);
                delete this.appServerKeepAliveTimeoutId;
            }
        }

        function _checkSessionState() {
            /*jshint validthis: true */
            var timeLeft;

            this.logger.debug("_checkSessionState: It is time to check if the session expiry popup should be shown");

            // Only if the touched flag is no set to true (there was no activity in the last AUTHENTICATION_CHECK_INTERVAL moments
            if (!this.isTouched) {
                this.logger.debug("_checkSessionState: User is inactive, continue to check for popup");
                // How long we still have for the current session?
                timeLeft = _getSessionTimeLeft.call(this);

                if (timeLeft > LEConfig.Session.WarnBeforePeriod * 1000) {
                    this.logger.debug("_checkSessionState: Set the timer for showing the popup in " + (timeLeft - LEConfig.Session.WarnBeforePeriod * 1000) + "millis of inactivity");
                    // This means that we still have more time on the session than the time we should warn
                    // Might happen, but not expected - keep on watching
                    this.timeoutId = setTimeout(_checkSessionState.bind(this), timeLeft - LEConfig.Session.WarnBeforePeriod * 1000);
                } else if (timeLeft > 0) {
                    this.logger.debug("_checkSessionState: Popup should be shown");
                    // First lets check that there is no notification on now
                    if (!this.notification) {
                        // Show notification for session about to expire
                        this.notification = this.notifier.showGlobalConfirm({
                            title:this.translator.translate('LEFramework.keepAlive.timeout.title'),
                            content:this.translator.translate('LEFramework.keepAlive.timeout.message'),
                            objectAction:{
                                text:this.translator.translate('LEFramework.keepAlive.timeout.okText'),
                                callback: _forceSessionRefresh.bind(this),
                                textAdditionalButton:this.translator.translate("LEFramework.signOutConfirmation.okText"),
                                cancelCallback: _forceSessionExpire.bind(this)
                            }
                        });
                    }

                    // In case the user does nothing or cancel set timer for shorter time - for expiration
                    this.logger.debug("_checkSessionState: Set the expiration timer");
                    this.expirationTimeoutId = setTimeout(_checkSessionState.bind(this), timeLeft);
                } else {
                    // Check if session had been sustained from a different
                    // In this case, if the last inform is future, session had already been sustained
                    this.logger.debug("_checkSessionState: Check if session had been sustained from a different browser tab");
                    var lastInform = _getSessionStartTime.call(this) + (LEConfig.Session.WarnBeforePeriod * 1000);
                    if (lastInform > ((new Date()).getTime())) {
                        // Session had been sustained from a different browser tab - Just restart
                        this.logger.debug("_checkSessionState: Session had been sustained from a different browser tab - Just restart");
                        this.start();
                    } else {
                        this.logger.debug("_checkSessionState: Current session is invalid, Going to force logout");
                        _forceSessionExpire.call(this);
                    }
                }
            }
        }

        function _getSessionLifeTime() {
            /*jshint validthis: true */
            return Number(this.sessionManager.getAccountSettingValueByID(this.sessionManager.ACCOUNT_SETTINGS_ID.SESSION_TIMEOUT)) || LEConfig.Session.LeServer * 1000;
        }

        function _getSessionStartTime() {
            /*jshint validthis: true */
            this.sessionStartTime = this.sessionManager.getLastTouched() || this.sessionStartTime || (new Date()).getTime();
            return this.sessionStartTime;
        }

        function _getUnTouchedPeriod() {
            /*jshint validthis: true */
            return ((new Date()).getTime() - _getSessionStartTime.call(this));
        }

        function _getSessionTimeLeft() {
            /*jshint validthis: true */
            return _getSessionLifeTime.call(this) - _getUnTouchedPeriod.call(this);
        }

        function _forceSessionRefresh() {
            /*jshint validthis: true */
            this.stop();
            this.isTouched = true;
            _checkAuthentication.call(this);
        }

        function _forceSessionExpire(options) {
            /*jshint validthis: true */
            this.stop();
            this.sessionManager.expire(options);
        }

        // "public" methods
        function initialize(options) {
            /*jshint validthis: true */
            this.translator = options.translator;
            this.notifier = options.notifier;
            this.sessionManager = options.sessionManager;
            this.logger = Logger.getLogger("LEFramework");

            this.logger.debug("initialize: sessionKeepAlive was initialized");
        }

        function start() {
            /*jshint validthis: true */
            this.logger.debug("start: First need to stop all existing timers");
            this.stop();

            this.logger.debug("start: Restart all needed timers");
            _getSessionStartTime.call(this);

            this.isTouched = false;
            this.authTimeoutId = setTimeout(_checkAuthentication.bind(this), AUTHENTICATION_CHECK_INTERVAL);

            _checkSessionState.call(this);
        }

        function startAppServerKeepAlive() {
            /*jshint validthis: true */
            this.logger.debug("startAppServerKeepAlive: start AppServer Session");
            this.appServerKeepAliveTimeoutId = setTimeout(_appServerKeepAlive.bind(this), APP_SERVER_KEEP_ALIVE_SESSION_INTERVAL);
        }

        function touched() {
            /*jshint validthis: true */
            var timeLeft;

            if (!this.isTouched) {
                this.logger.debug("touched: User is back to activity, Lets check if session should be refreshed now");
                timeLeft = _getSessionTimeLeft.call(this);

                if (timeLeft <= (AUTHENTICATION_CHECK_INTERVAL + MAGIC_NUMBER_TIME_DIFF)) {
                    this.logger.debug("touched: The current registered timer, will not be able to refresh the session on time, Going to force a session refresh now!");
                    _forceSessionRefresh.call(this);
                } else {
                    this.logger.debug("touched: The current registered timer, will refresh the session on next tick! Just mark the activity and do nothing else for now");
                    this.isTouched = true;
                }
            }
        }

        function stop() {
            /*jshint validthis: true */
            this.logger.debug("stop: Stopping all timers");
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
                delete this.timeoutId;
            }
            if (this.expirationTimeoutId) {
                // Clear remaining expiration timer
                clearTimeout(this.expirationTimeoutId);
                delete this.expirationTimeoutId;
            }
            if (this.authTimeoutId) {
                // Clear remaining authenticated timer
                clearTimeout(this.authTimeoutId);
                delete this.authTimeoutId;
            }
            if (this.notification) {
                this.notification.close();
                delete this.notification;
            }

            delete this.sessionStartTime;
            delete this.isTouched;
        }

        // Revealing public methods
        return {
            initialize:initialize,
            start:start,
            startAppServerKeepAlive:startAppServerKeepAlive,
            touched:touched,
            stop:stop
        };
    })();

    return SessionKeepAlive;
});
